import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import CardCarousel from 'components/Luxkit/Carousel/CardCarousel'
import LineArrowsRightDownIcon from 'components/Luxkit/Icons/line/LineArrowsRightDownIcon'
import LineBedIcon from 'components/Luxkit/Icons/line/LineBedIcon'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import StructureLabel from 'components/Luxkit/Label/StructureLabel'
import Heading from 'components/Luxkit/Typography/Heading'
import Group from 'components/utils/Group'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { EmptyArray, groupBy } from 'lib/array/arrayUtils'
import React, { useMemo } from 'react'
import { FlightsInfoByRoom, getAvailablePackagesByRoom, getFlightsInfoByRoom, PackagesOptionsViewsByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import styled from 'styled-components'
import MobileMultipleRoomsCarouselPackageOption from './MobileMultipleRoomsCarouselPackageOption'
import { rem } from 'polished'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import TextLink from 'components/Luxkit/TextLink'
import MobileMultipleRoomsRoomOptionPriceDetails from './MobileMultipleRoomsRoomOptionPriceDetails'
import Pane from 'components/Common/Pane'
import AccommodationRoomTile from 'components/AccommodationKit/AccommodationRoomTile'
import AccommodationRoomTileImage from 'components/AccommodationKit/AccommodationRoomTileImage'
import TextButton from 'components/Luxkit/Button/TextButton'
import LineShoppingCartIcon from 'components/Luxkit/Icons/line/LineShoppingCartIcon'

const Container = styled.div`
  padding: ${rem(20)};
`

interface Props {
  rooms: Array<App.RoomOccupants>;
  flightsInfoByRoom: FlightsInfoByRoom;
  availablePackagesByRoom: PackagesOptionsViewsByRoom;
  onSelectIndividual: () => void;
}

function MobileMultipleRoomsSameRoomContent(props: Props) {
  const { rooms, availablePackagesByRoom, flightsInfoByRoom, onSelectIndividual } = props
  const availablePackages = availablePackagesByRoom[rooms[0].roomId]

  const isFlightsForAllRoomsAvailable = useMemo(() => Object.values(flightsInfoByRoom).every(flightsInfo => flightsInfo.isFlightsAvailable), [flightsInfoByRoom])

  const optionsByRoomType = useMemo(() =>
    Array
      .from(groupBy(availablePackages, (pkg) => pkg?.package.roomType?.id).values())
      // Order by availiabilty of rooms in the room type from having the most to the least
      .sort((a, b) => ((b[0].availableRooms ?? 0) - rooms.length) - ((a[0].availableRooms ?? 0) - rooms.length)),
  [availablePackages, rooms.length])

  return (
    <Pane background="neutral-six">
      <Group direction="vertical" gap={8}>
        {optionsByRoomType.map((roomOption) => {
          if (roomOption.length === 0) {
            return null
          }

          // Same room type for all options, so we pick the first one
          const roomType = roomOption[0].package.roomType
          if (!roomType) {
            return null
          }

          const pkg = roomOption[0].package as App.HotelPackage
          const isAllRoomsAvailable = (roomOption[0].availableRooms ?? 0) >= rooms.length

          return (
            <Pane key={roomType.id}>
              <AccommodationRoomTile roomPackage={pkg}>
                <AccommodationRoomTileImage roomPackage={pkg} />
              </AccommodationRoomTile>
              <Container>
                <Group direction="vertical" gap={16}>
                  <Group direction="vertical" gap={12}>
                    <Group direction="horizontal" gap={8}>
                      <StructureLabel variant="standard">{rooms.length}x</StructureLabel>
                      <Heading variant="heading4">{roomType.name}</Heading>
                    </Group>
                    <LabelGroup>
                      {!!roomOption[0]?.availableRooms && <RoomsRemainingLabel roomCount={roomOption[0]?.availableRooms} threshold="rate" />}
                      {!!roomType.stats.booked && <BookingsCountLabel bookings={roomType.stats.booked} threshold="room" />}
                    </LabelGroup>
                    <LabelGroup>
                      {!!roomType.sizeSqm && <StructureLabel variant="light" startIcon={<LineArrowsRightDownIcon />}>{roomType.sizeSqm} m&sup2;</StructureLabel>}
                      {!!roomType.numberOfBedrooms && <StructureLabel variant="light" startIcon={<LineBedIcon />}>{roomType.numberOfBedrooms} bedrooms</StructureLabel>}
                    </LabelGroup>
                  </Group>
                  {!isAllRoomsAvailable && (
                    <Group direction="vertical" gap={12}>
                      <MobileMultipleRoomsRoomOptionPriceDetails
                        room={rooms[0]}
                        packageOption={roomOption[0]}
                      />
                      <MessageBanner
                        kind="informational-primary"
                        description={<>
                          This room type works for some rooms in your group, but not all. To select it, try{' '}
                          <TextLink onClick={onSelectIndividual}>choosing rooms individually</TextLink>.
                        </>}
                      />
                    </Group>
                  )}
                  {isAllRoomsAvailable && (
                    <CardCarousel snap="start" width={roomOption.length === 1 ? '100%' : 'auto'} gap={12}>
                      {roomOption.map(packageView => <MobileMultipleRoomsCarouselPackageOption
                        key={packageView.package.uniqueKey}
                        packageView={packageView}
                        childrenCtaDirection="vertical"
                      >
                        <>
                          {isFlightsForAllRoomsAvailable && (<Group direction="horizontal" gap={8} horizontalAlign="stretch">
                            <TextButton kind="secondary" startIcon={<LineShoppingCartIcon />} fit="flex">Add to cart</TextButton>
                            <TextButton kind="primary" fit="flex">Book {rooms.length} rooms</TextButton>
                          </Group>)}
                          {!isFlightsForAllRoomsAvailable && <TextButton kind="primary" fit="flex">Select {rooms.length} rooms</TextButton>}
                        </>
                      </MobileMultipleRoomsCarouselPackageOption>)}
                    </CardCarousel>
                  )}
                </Group>
              </Container>
            </Pane>
          )
        })}
      </Group>
    </Pane>
  )
}

type MappedProps = Pick<Props,
  'rooms'
  | 'flightsInfoByRoom'
  | 'availablePackagesByRoom'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  rooms: state.rooms ?? EmptyArray as Array<App.RoomOccupants>,
  flightsInfoByRoom: getFlightsInfoByRoom(state, appState),
  availablePackagesByRoom: getAvailablePackagesByRoom(state, appState),
}), MobileMultipleRoomsSameRoomContent)
